import React, { ReactNode } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { ChangeControlRefusal } from '../hooks/useSubmitTranslation';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/** Resource id of the dialog header. */
export const CHANGE_CONTROL_BLOCKED_HEADER_KEY = 'sgd-cc-blocked-header';

/** Resource id of the default recovery text for the Submit flow, where the draft survives the refusal. */
export const SUBMIT_REFUSAL_RECOVERY_KEY = 'sgd-cc-blocked-recovery';

interface ChangeControlBlockedModalProps {
  /** The refusal to show; `null` renders nothing (no dialog without a message). */
  refusal: ChangeControlRefusal | null;
  /**
   * Next-step text. Omit to get the Submit sentence; Rollback passes its own.
   *
   * Resolved inside the component rather than as a default parameter value,
   * because the default is a localized resource and `t` only exists once the
   * hook has run.
   */
  recovery?: ReactNode;
  onDismiss: () => void;
}

/**
 * Dialog for a change-control refusal. Unlike other submit failures (Flashbar),
 * the operator must act on this before retrying, so it must be read, not just
 * noticed. The service message is shown verbatim; `code` is for tests and
 * telemetry only.
 *
 * Returns `null` instead of `visible={false}`: Cloudscape Modal renders its
 * footer even when hidden, leaving a stray Close button for assistive tech.
 */
const ChangeControlBlockedModal: React.FC<ChangeControlBlockedModalProps> = ({
  refusal,
  recovery,
  onDismiss,
}) => {
  const { t } = useTranslation();
  if (!refusal) {
    return null;
  }

  return (
    <Modal
      visible={true}
      onDismiss={onDismiss}
      header={t(CHANGE_CONTROL_BLOCKED_HEADER_KEY)}
      closeAriaLabel={t('sgd-cc-blocked-close')}
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            {t('sgd-close')}
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="error" statusIconAriaLabel={t('status-icon-error')}>
          {refusal.message}
        </Alert>
        <Box variant="p">{recovery ?? t(SUBMIT_REFUSAL_RECOVERY_KEY)}</Box>
      </SpaceBetween>
    </Modal>
  );
};

export default ChangeControlBlockedModal;
